<template>
  <b-form v-if="true || (isLUPCTown === false) || ((isLUPCTown === true) && (new Date() >= new Date('2026-01-01')))">
    <ValidationProvider
      name="Total Harvest Area"
      :rules="{
        required: true,
        max_decimal_singular: 1,
        min_value: 0.1,
        greaterThanOrEqual: 'Land Use Change Area',
        lessThanOrEqualPropertyAcreage: notification.parcelAcreage,
      }"
      v-slot="{ errors, classes }"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="totalHarvestArea">
        <template v-slot:label>
          <span class="req-ind">*</span>
          <span>How many acres do you anticipate harvesting?</span>
        </template>
        <b-form-input
          type="number"
          id="totalHarvestArea"
          name="totalHarvestArea"
          v-model="harvestDetails.totalHarvestArea"
          placeholder="Enter Total Harvest Area"
          :class="classes"
          maxlength="7"
          :aria-describedby="$errorsIdString(errors, 'totalHarvestArea')"
          :disabled="mfsAdmin"
        />
        <InputErrors :errors="errors" errorsIdPrefix="totalHarvestArea" />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="Tax Law"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="taxLaw">
        <template v-slot:label>
          <span class="req-ind">*</span>
          <span>Is the land that you intend to harvest taxed under the Maine Tree Growth Tax Law?</span>
        </template>
        <b-form-radio-group
          id="taxLaw"
          name="taxLaw"
          v-model="harvestDetails.taxLaw"
          :aria-describedby="$errorsIdString(errors, 'taxLaw')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="taxLaw"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <LandUseChangeForm :harvestDetails="harvestDetails" />
    <ValidationProvider
      name="20 Acres Clearcut"
      rules="required"
      v-slot="{ errors }"
      v-if="harvestDetails.totalHarvestArea > 20"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="clearcut20">
        <template v-slot:label>
          <b-row no-gutters>
            <b-col cols="11" sm="auto">
              <span class="req-ind">*</span>
              <span>Will any of the harvested areas be clearcuts larger than 20 acres?</span>
            </b-col>
            <b-col cols="1" sm="1">
              <FontAwesomeIcon
                focusable="true"
                tabindex="0"
                class="mx-2 text-primary align-self-center"
                icon="info-circle"
                size="lg"
                id="clearcutWarning20"
              />
              <b-popover
                target="clearcutWarning20"
                :triggers="['hover', 'focus']"
                placement="right"
              >
                When calculating clearcut acres, do not count acres that will
                be converted to another use.
              </b-popover>
            </b-col>
          </b-row>
        </template>
        <b-form-radio-group
          id="clearcut20"
          name="clearcut20"
          v-model="harvestDetails.clearcut20"
          :aria-describedby="$errorsIdString(errors, 'clearcut20')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="clearcut20"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="75 Acres Clearcut"
      rules="required"
      v-slot="{ errors }"
      v-if="harvestDetails.totalHarvestArea > 75"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="clearcut75">
        <template v-slot:label>
          <b-row no-gutters>
            <b-col cols="11" sm="auto">
              <span class="req-ind">*</span>
              <span>Will any of the harvested areas be clearcuts larger than 75 acres?</span>
            </b-col>
            <b-col cols="1" sm="1">
              <FontAwesomeIcon
                focusable="true"
                tabindex="0"
                class="mx-2 text-primary align-self-center"
                icon="info-circle"
                size="lg"
                id="clearcutWarning75"
              />
              <b-popover
                target="clearcutWarning75"
                :triggers="['hover', 'focus']"
                placement="right"
              >
                When calculating clearcut acres, do not count acres that will
                be converted to another use.
              </b-popover>
            </b-col>
          </b-row>
        </template>
        <b-form-radio-group
          id="clearcut75"
          name="clearcut75"
          v-model="harvestDetails.clearcut75"
          :aria-describedby="$errorsIdString(errors, 'clearcut75')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="clearcut75"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="Wood to Stream"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="woodToStream">
        <template v-slot:label>
          <span class="req-ind">*</span>
          Will you be adding wood to a stream that is consistent with
          <a
            :href="links.chapter25Rules"
            target="_blank"
            rel="noopener noreferrer">
            MFS Chapter 25 Rule</a>? (Note: If yes, you will be required to submit a harvest plan.)
        </template>
        <b-form-radio-group
          id="woodToStream"
          name="woodToStream"
          v-model="harvestDetails.woodToStream"
          :aria-describedby="$errorsIdString(errors, 'woodToStream')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="woodToStream"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="Water Feature"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <div role="group" class="form-row form-group justify-content-between">
        <label id="waterFeatureLabel" for="waterFeature">
          <span class="req-ind">*</span>
          <span>Will any harvesting be <strong>within the shoreland area</strong>?**</span>
        </label>
        <b-form-radio-group
          id="waterFeature"
          name="waterFeature"
          v-model="harvestDetails.waterFeatureTypeId"
          :aria-describedby="$errorsIdString(errors, 'waterFeature')"
          :disabled="mfsAdmin"
        >
        <b-form-radio
            v-for="waterFeatureType in waterFeatureTypes"
            :key="waterFeatureType.id"
            class="noselect"
            :value="waterFeatureType.id"
          >
            <span class="sr-only">Choose water feature type</span>
            {{ waterFeatureType.name }}
          </b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="waterFeature"
          class="d-block invalid-feedback text-right"
        />
      </div>
    </ValidationProvider>
    <HarvestSWSForm
      :harvestDetails="harvestDetails"
      :waterFeatureTypeId="harvestDetails.waterFeatureTypeId"
    />
  </b-form>
  <b-form v-else>
    <ValidationProvider
      name="Total Harvest Area"
      :rules="{
        required: true,
        max_decimal_singular: 1,
        min_value: 0.1,
        greaterThanOrEqual: 'Land Use Change Area',
        lessThanOrEqualPropertyAcreage: notification.parcelAcreage,
      }"
      v-slot="{ errors, classes }"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="totalHarvestArea">
        <template v-slot:label>
          <span class="req-ind">*</span>How many acres do you anticipate harvesting?
        </template>
        <b-form-input
          type="number"
          id="totalHarvestArea"
          name="totalHarvestArea"
          v-model="harvestDetails.totalHarvestArea"
          placeholder="Enter Total Harvest Area"
          :class="classes"
          maxlength="7"
          :aria-describedby="$errorsIdString(errors, 'totalHarvestArea')"
          :disabled="mfsAdmin"
        />
        <InputErrors :errors="errors" errorsIdPrefix="totalHarvestArea" />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="Tax Law"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="taxLaw">
        <template v-slot:label>
          <span class="req-ind">*</span>Is the land that you intend to harvest taxed under the
          Maine Tree Growth Tax Law?
        </template>
        <b-form-radio-group
          id="taxLaw"
          name="taxLaw"
          v-model="harvestDetails.taxLaw"
          :aria-describedby="$errorsIdString(errors, 'taxLaw')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="taxLaw"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <LandUseChangeForm :harvestDetails="harvestDetails" />
    <ValidationProvider
      name="20 Acres Clearcut"
      rules="required"
      v-slot="{ errors }"
      v-if="harvestDetails.totalHarvestArea>20"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="clearcut20">
        <template v-slot:label>
          <b-row no-gutters>
            <b-col cols="11" sm="auto">
              <span class="req-ind">*</span>Will any of the harvested areas be clearcuts larger than
              20 acres?
            </b-col>
            <b-col cols="1" sm="1">
              <FontAwesomeIcon
                focusable="true"
                tabindex="0"
                class="mx-2 text-primary align-self-center"
                icon="info-circle"
                size="lg"
                id="clearcutWarning20"
              />
              <b-popover
                target="clearcutWarning20"
                :triggers="['hover', 'focus']"
                placement="right"
              >
                When calculating clearcut acres, do not count acres that will
                be converted to another use.
              </b-popover>
            </b-col>
          </b-row>
        </template>
        <b-form-radio-group
          id="clearcut20"
          name="clearcut20"
          v-model="harvestDetails.clearcut20"
          :aria-describedby="$errorsIdString(errors, 'clearcut20')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="clearcut20"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="75 Acres Clearcut"
      rules="required"
      v-slot="{ errors }"
      v-if="harvestDetails.totalHarvestArea>75"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="clearcut75">
        <template v-slot:label>
          <b-row no-gutters>
            <b-col cols="11" sm="auto">
              <span class="req-ind">*</span>Will any of the harvested areas be clearcuts larger
              than 75 acres?
            </b-col>
            <b-col cols="1" sm="1">
              <FontAwesomeIcon
                focusable="true"
                tabindex="0"
                class="mx-2 text-primary align-self-center"
                icon="info-circle"
                size="lg"
                id="clearcutWarning75"
              />
              <b-popover
                target="clearcutWarning75"
                :triggers="['hover', 'focus']"
                placement="right"
              >
                When calculating clearcut acres, do not count acres that will
                be converted to another use.
              </b-popover>
            </b-col>
          </b-row>
        </template>
        <b-form-radio-group
          id="clearcut75"
          name="clearcut75"
          v-model="harvestDetails.clearcut75"
          :aria-describedby="$errorsIdString(errors, 'clearcut75')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="clearcut75"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="Water Feature"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="waterFeature">
        <template v-slot:label>
          <span class="req-ind">*</span>
          Will any harvesting be <strong>within a 250-foot wide regulated area</strong> adjacent to a great
          pond, river or regulated wetland, and/or <strong>within a 75-foot wide regulated area</strong>
          adjacent to a stream (including unmapped streams)?
        </template>
        <b-form-radio-group
          id="waterFeature"
          name="waterFeature"
          v-model="harvestDetails.waterFeature"
          :aria-describedby="$errorsIdString(errors, 'waterFeature')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="waterFeature"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      name="Wood to Stream"
      rules="required"
      v-slot="{ errors }"
      slim
    >
      <b-form-group
        label-cols="12"
        label-cols-sm="7"
        content-cols-sm="5"
        label-for="woodToStream">
        <template v-slot:label>
          <span class="req-ind">*</span>Will you be adding wood to a stream that is consistent
          with
          <a
            :href="links.chapter25Rules"
            target="_blank"
            rel="noopener noreferrer">
            MFS Chapter 25 Rule</a>? (Note: If yes, you will be required to submit a harvest plan.)
        </template>
        <b-form-radio-group
          id="woodToStream"
          name="woodToStream"
          v-model="harvestDetails.woodToStream"
          :aria-describedby="$errorsIdString(errors, 'woodToStream')"
          :disabled="mfsAdmin"
        >
          <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
          <b-form-radio class="noselect" :value="false">No</b-form-radio>
        </b-form-radio-group>
        <InputErrors
          :errors="errors"
          errorsIdPrefix="woodToStream"
          class="d-block invalid-feedback text-right"
        />
      </b-form-group>
    </ValidationProvider>
    <HarvestSWSForm :harvestDetails="harvestDetails" />
  </b-form>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import HarvestSWSForm from './HarvestSWSForm.vue';
import LandUseChangeForm from './LandUseChangeForm.vue';
import LookupModules from '@/store/moduleConfig';

export default {
  name: 'HarvestDetailsForm',
  components: {
    HarvestSWSForm,
    LandUseChangeForm,
  },
  props: {
    harvestDetails: {
      type: Object,
      default() {
        return {
          totalHarvestArea: null,
          taxLaw: null,
          landUseChange: null,
          landUseChangeCategoryId: null,
          landUseChangeArea: null,
          clearcut20: null,
          clearcut75: null,
          woodToStream: null,
          sws250ComplianceTypeId: null,
          sws75ComplianceTypeId: null,
          waterFeatureTypeId: null,
        };
      },
    },
    isLUPCTown: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState('Configuration', {
      links: state => state.links,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    ...mapState(LookupModules.WATER_FEATURE_TYPES.namespace, {
      waterFeatureTypes: state => state.options,
      waterFeatureTypesLoading: state => state.loading,
    }),
    ...mapGetters('User', ['mfsAdmin']),
  },
  created() {
    this.$store.dispatch(`${LookupModules.WATER_FEATURE_TYPES.namespace}/fetchOptions`);
  },
};
</script>
